/**
 * Envío UI-2 · Resultados del canvas (sólo en la Vista Edición del layout nuevo).
 *
 * Franja plegable arriba del timeline con lo que ya generaron los nodos.
 * "Agregar" usa el MISMO camino que el botón "Timeline" de cada nodo
 * (sendAssetToTimeline): misma resolución del asset, mismo dedupe, misma
 * trazabilidad {project_id, node_id}. No hace pedidos propios.
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Plus, Loader2, Music, Film } from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { sendAssetToTimeline } from '../../utils/sendAssetToTimeline';
import { collectCanvasResults } from '../../layout/canvasResults';

const OPEN_KEY = 'sml.v2.binOpen';
const readOpen = () => { try { return localStorage.getItem(OPEN_KEY) !== '0'; } catch { return true; } };

const ResultCard = ({ item }) => {
  const [busy, setBusy] = useState(false);
  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);
  const add = async () => {
    setBusy(true);
    try {
      await sendAssetToTimeline(item.assetId, {
        fallbackUrl: item.url,
        fallbackKind: item.kind,
        label: item.label,
        source: { project_id: useWorkflowStore.getState().currentProjectId, node_id: item.nodeId },
        onResolved: (asset) => {
          if (asset.id === item.assetId && asset.url === item.url) return;
          updateNodeData(item.nodeId, { assetId: asset.id, outputUrl: asset.url, outputType: asset.media_kind || item.kind });
        },
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`tl-bin-card is-${item.kind}`} title={item.label} data-testid="tl-bin-card">
      <div className="tl-bin-thumb">
        {item.kind === 'image' && <img src={item.thumb} alt="" loading="lazy" />}
        {item.kind === 'video' && (
          <>
            <video src={`${item.url}#t=0.5`} muted preload="metadata" playsInline />
            <span className="tl-bin-kind"><Film size={11} /> Video</span>
          </>
        )}
        {item.kind === 'audio' && <span className="tl-bin-audio"><Music size={18} /> Audio</span>}
      </div>
      <div className="tl-bin-foot">
        <span className="tl-bin-label">{item.label}</span>
        <button
          type="button"
          className="tl-bin-add"
          onClick={add}
          disabled={busy}
          title="Agregar al timeline"
          aria-label={`Agregar ${item.label} al timeline`}
          data-testid="tl-bin-add"
        >
          {busy ? <Loader2 size={12} className="spin" /> : <Plus size={12} />}
        </button>
      </div>
    </div>
  );
};

/**
 * I-42 · con el preview en L la franja está plegada para dar lugar al preview.
 * Tocar «Resultados del canvas» ya no queda mudo: pasa el preview a M
 * (`onNeedRoom`), abre la franja y avisa, con la opción de volver a L.
 */
const CanvasResultsBin = ({ forceClosed = false, onNeedRoom, onRestoreLarge }) => {
  const nodes = useWorkflowStore((s) => s.nodes);
  const items = useMemo(() => collectCanvasResults(nodes), [nodes]);
  const [open, setOpen] = useState(readOpen);
  const shown = open && !forceClosed;
  const remember = (next) => {
    try { localStorage.setItem(OPEN_KEY, next ? '1' : '0'); } catch { /* privado */ }
  };
  const toggle = () => {
    if (forceClosed && onNeedRoom) {
      onNeedRoom();
      setOpen(true);
      remember(true);
      toast('Pasamos la vista previa a tamaño M para mostrar los resultados del canvas.', {
        id: 'tl-bin-room',
        duration: 5000,
        action: onRestoreLarge ? { label: 'Volver a L', onClick: onRestoreLarge } : undefined,
      });
      return;
    }
    setOpen((v) => { remember(!v); return !v; });
  };

  return (
    <section className={`tl-bin ${shown ? 'is-open' : 'is-closed'}`} aria-label="Resultados del canvas" data-testid="tl-bin">
      <div className="tl-bin-head">
        <button type="button" className="tl-bin-toggle" onClick={toggle} aria-expanded={shown} data-testid="tl-bin-toggle">
          {shown ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          Resultados del canvas · {items.length}
        </button>
        <span className="tl-bin-hint">
          {forceClosed ? 'Plegado para dar lugar al preview grande · tocá para verlos'
            : items.length ? 'Lo que generan los nodos aparece acá. Tocá + para sumarlo al timeline.'
              : 'Cuando un nodo termine de generar, su resultado aparece acá.'}
        </span>
      </div>
      {shown && items.length > 0 && (
        <div className="tl-bin-row">
          {items.map((it) => <ResultCard key={it.key} item={it} />)}
        </div>
      )}
    </section>
  );
};

export default CanvasResultsBin;
